import { cloneElement } from 'react';
import type React from 'react';

import { ErrorMessage } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { WithTokenParams } from '#src/app/shared/types/payment';
import type { PaymentMethodSaveOptions, PaymentType } from '#src/app/shared/utils/payments';
import { guardStripeFocusTracker } from '#src/app/shared/utils/payments';

import { StyleWrapper } from '../../../components/StyleWrapper';

import { IbanElementWrapper } from './components/IbanElementWrapper';
import { useIbanForm } from './hooks/useIbanForm';

type Props = {
	name: string;
	email: string;
	currency: string;
	countryCode: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	validate: (paymentType: PaymentType) => Promise<boolean>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	withToken: (params: WithTokenParams) => Promise<void>;
	onFieldFocus: () => void;
	submitButton: React.ReactElement<{
		disabled: boolean;
		loading: boolean;
		label: React.JSX.Element;
		onClick?: () => void;
	}>;
};

export const IbanForm: React.FC<Props> = ({
	name,
	email,
	currency,
	countryCode,
	paymentMethodSaveOptions,
	beforeToken,
	validate,
	prePaymentChecks,
	onTokenError,
	onTokenInvalid,
	withToken,
	onFieldFocus,
	submitButton,
}) => {
	const { translate } = useI18n();
	const {
		data: { validationError, errorMessage },
		actions: { handleSubmit, validateStripeElementOnChange },
	} = useIbanForm({
		name,
		email,
		paymentMethodSaveOptions,
		currency,
		countryCode,
		beforeToken,
		validate,
		prePaymentChecks,
		onTokenError,
		onTokenInvalid,
		withToken,
	});

	return (
		<div className="flex flex-col gap-1">
			<form data-testid="sepa-form" className="flex flex-col gap-6" onSubmit={handleSubmit}>
				<div data-qa="sepa-iframe-form-element">
					<StyleWrapper
						label={translate('fe.components.sepa.account_number')}
						errorMessage={validationError ?? undefined}
						onFocus={guardStripeFocusTracker(onFieldFocus, 'iban')}
					>
						<IbanElementWrapper onChange={validateStripeElementOnChange} />
					</StyleWrapper>
				</div>
				<div className="flex justify-center">
					{cloneElement(submitButton, {
						disabled: submitButton.props.disabled,
					})}
				</div>
			</form>
			{errorMessage && (
				<div data-qa="iban-form-error-message">
					<ErrorMessage className="mb-4" size="large">
						{errorMessage}
					</ErrorMessage>
				</div>
			)}
		</div>
	);
};
